<section class="recipes-page">
  <header class="page-header">
    <div>
      <h1>Recettes</h1>
      <p>Créer, modifier, supprimer et importer des recettes au format JSON.</p>
    </div>

    <div class="header-actions">
      <button type="button" class="ghost" (click)="loadRecipes()" [disabled]="loading">Rafraîchir</button>
      <button type="button" class="primary" (click)="startCreate()" [disabled]="saving">Nouvelle recette</button>
    </div>
  </header>

  <p class="message error" *ngIf="errorMessage">{{ errorMessage }}</p>
  <p class="message success" *ngIf="successMessage">{{ successMessage }}</p>

  <div class="layout" [class.editor-open]="showEditor">
    <section class="panel list" [class.full-width]="!showEditor">
      <div class="list-header">
        <h2>Toutes les recettes</h2>
        <input
          type="search"
          [value]="searchQuery"
          (input)="applySearch(($any($event.target).value || '').toString())"
          placeholder="Rechercher une recette..."
        />
      </div>

      <div class="state loading-state" *ngIf="loading">
        <span class="spinner" aria-hidden="true"></span>
        <span>Chargement…</span>
      </div>
      <div class="state" *ngIf="!loading && recipes.length === 0">Aucune recette pour le moment.</div>

      <div class="recipes-grid" *ngIf="!loading && recipes.length > 0">
        <article class="recipe-card" *ngFor="let recipe of recipes">
          <div class="recipe-cover">
            <ng-container *ngIf="coverImageUrl(recipe) as coverUrl; else noCover">
              <img [src]="coverUrl" [alt]="recipe.name" />
            </ng-container>
            <ng-template #noCover>
              <div class="cover-placeholder">Aucune image</div>
            </ng-template>
          </div>

          <div class="recipe-main">
            <strong>{{ recipe.name }}</strong>
            <span>{{ categoryLabel(recipe.categoryId) }}</span>
            <span>{{ recipe.creatorType || 'USER' }}</span>
          </div>

          <div class="recipe-actions">
            <button
              type="button"
              class="ghost"
              (click)="startEdit(recipe)"
              [disabled]="saving || importRunning"
              *ngIf="canEdit(recipe)"
            >
              Modifier
            </button>
            <button
              type="button"
              class="danger"
              (click)="deleteRecipe(recipe)"
              [disabled]="saving || importRunning || isDeleting(recipe.id)"
              *ngIf="canDelete(recipe)"
            >
              {{ isDeleting(recipe.id) ? 'Suppression…' : 'Supprimer' }}
            </button>
            <span class="readonly" *ngIf="!canEdit(recipe) && !canDelete(recipe)">Lecture seule</span>
          </div>
        </article>
      </div>

      <footer class="pagination">
        <span>{{ totalItems }} résultat(s)</span>
        <div>
          <button type="button" class="ghost" (click)="previousPage()" [disabled]="currentPage === 0">Préc.</button>
          <span>Page {{ currentPage + 1 }} / {{ totalPages }}</span>
          <button type="button" class="ghost" (click)="nextPage()" [disabled]="currentPage + 1 >= totalPages">Suiv.</button>
        </div>
      </footer>
    </section>

    <section class="panel editor" *ngIf="showEditor">
      <h2>{{ editingRecipeId == null ? 'Ajouter une recette' : 'Modifier la recette #' + editingRecipeId }}</h2>

      <form [formGroup]="form" (ngSubmit)="submitForm()">
        <label>
          <span>Nom</span>
          <input type="text" formControlName="name" placeholder="Nom de la recette" />
        </label>

        <label>
          <span>Description</span>
          <textarea rows="4" formControlName="description" placeholder="Description"></textarea>
        </label>

        <div class="grid two">
          <label>
            <span>Coût</span>
            <select formControlName="cost">
              <option *ngFor="let cost of costs" [value]="cost">{{ cost }}</option>
            </select>
          </label>

          <label>
            <span>Difficulté</span>
            <select formControlName="difficulty">
              <option *ngFor="let difficulty of difficulties" [value]="difficulty">{{ difficulty }}</option>
            </select>
          </label>
        </div>

        <div class="grid two">
          <label>
            <span>Temps de cuisson (min)</span>
            <input type="number" min="1" formControlName="cookingTime" />
          </label>

          <label>
            <span>Nombre de personnes</span>
            <input type="number" min="1" formControlName="numberOfPersons" />
          </label>
        </div>

        <div class="grid two">
          <label>
            <span>Catégorie</span>
            <select formControlName="categoryId">
              <option [ngValue]="null">Sélectionner</option>
              <option *ngFor="let category of categories" [ngValue]="category.id">
                {{ category.name || '#' + category.id }}
              </option>
            </select>
          </label>

          <label>
            <span>Access type</span>
            <select formControlName="accessType">
              <option *ngFor="let accessType of accessTypes" [value]="accessType">{{ accessType }}</option>
            </select>
          </label>
        </div>

        <label>
          <span>Image de couverture</span>
          <input type="text" formControlName="coverImage" placeholder="images/cover.jpg ou cover.jpg" />
        </label>

        <label class="file-input">
          <span>Uploader image de couverture</span>
          <input type="file" accept="image/png,image/jpeg,image/webp" (change)="onCoverFileSelected($event)" />
          <small class="inline-loading" *ngIf="uploadingCover">
            <span class="spinner sm" aria-hidden="true"></span>
            <span>Upload en cours…</span>
          </small>
        </label>

        <label>
          <span>Codes pays (séparés par virgule)</span>
          <input type="text" formControlName="countries" placeholder="AO, BJ, CI" />
        </label>

        <div class="array-section" formArrayName="ingredients">
          <div class="array-header">
            <h3>Ingrédients</h3>
            <button type="button" class="ghost" (click)="addIngredient()">+ Ajouter</button>
          </div>

          <div class="array-item" *ngFor="let ingredient of ingredientsArray.controls; let i = index" [formGroupName]="i">
            <div class="grid two">
              <label>
                <span>Titre</span>
                <input type="text" formControlName="title" placeholder="2 oignons moyens" />
              </label>
              <label>
                <span>Contenu</span>
                <input type="text" formControlName="content" placeholder="Oignons" />
              </label>
            </div>

            <div class="grid three">
              <label>
                <span>Image</span>
                <input type="text" formControlName="image" placeholder="optionnel" />
              </label>
              <label>
                <span>Prix</span>
                <input type="number" formControlName="price" placeholder="optionnel" />
              </label>
              <label>
                <span>Prix formaté</span>
                <input type="text" formControlName="priceFormatted" placeholder="optionnel" />
              </label>
            </div>

            <div class="inline-actions">
              <label class="checkbox">
                <input type="checkbox" formControlName="mandatory" />
                <span>Obligatoire</span>
              </label>
              <button type="button" class="danger subtle" (click)="removeIngredient(i)">Retirer</button>
            </div>
          </div>
        </div>

        <div class="array-section" formArrayName="steps">
          <div class="array-header">
            <h3>Étapes</h3>
            <button type="button" class="ghost" (click)="addStep()">+ Ajouter</button>
          </div>

          <div class="array-item" *ngFor="let step of stepsArray.controls; let i = index" [formGroupName]="i">
            <div class="grid two">
              <label>
                <span>Titre</span>
                <input type="text" formControlName="title" placeholder="Mise en place" />
              </label>
              <label>
                <span>Image</span>
                <input type="text" formControlName="image" placeholder="optionnel" />
              </label>
            </div>

            <label>
              <span>Contenu</span>
              <textarea rows="3" formControlName="content" placeholder="Description de l’étape"></textarea>
            </label>

            <div class="inline-actions">
              <label class="checkbox">
                <input type="checkbox" formControlName="mandatory" />
                <span>Obligatoire</span>
              </label>
              <button type="button" class="danger subtle" (click)="removeStep(i)">Retirer</button>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="primary" [disabled]="saving || uploadingCover">
            {{ saving ? 'Enregistrement…' : editingRecipeId == null ? 'Créer la recette' : 'Mettre à jour' }}
          </button>
          <button type="button" class="ghost" (click)="closeEditor()" [disabled]="saving || uploadingCover">Fermer</button>
        </div>
      </form>
    </section>
  </div>

  <section class="panel import">
    <h2>Import JSON</h2>
    <p>Format attendu: tableau de recettes identique à <code>recipes.json</code>.</p>

    <div class="import-actions">
      <input type="file" accept=".json,application/json" (change)="onImportFileSelected($event)" />
      <button type="button" class="primary" (click)="importFromJsonFile()" [disabled]="!importFile || importRunning">
        {{ importRunning ? 'Import en cours…' : 'Importer les recettes' }}
      </button>
    </div>

    <p class="message success" *ngIf="importSummary">{{ importSummary }}</p>
    <ul class="failures" *ngIf="importFailures.length > 0">
      <li *ngFor="let failure of importFailures">{{ failure }}</li>
    </ul>
  </section>
</section>
